// ─────────────────────────────────────────────────────────────────────────────
// V2CreateInvoice — /dashboard/createinvoice in the v2 design.
// Business logic mirrors v1 CreateInvoice.js exactly:
//   • tax type from the first two GSTIN digits (same state → CGST_SGST, else IGST)
//   • calculateTotals(): Exclusive / Inclusive, discount spread pro-rata, per-item GST
//   • stock validation for inventory items, identical POST /api/invoices/addinvoice body
//   • goods fields only sent when company.businessType === "goods"
// New: inline line editing, searchable pickers, remembered invoice prefix,
// live summary + readiness checklist, success screen.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { Money, Segmented, inr, useMedia } from "./primitives";
import { Combo, Field, LineItems, PageHead, newLine, stockProblems, useClients, useProducts } from "./kit";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const today = () => new Date().toISOString().split("T")[0];
const PREFIX_KEY = "eb-v2-invoice-prefix";

const DOC_TYPES = [
  { value: "TAX_INVOICE", label: "Tax invoice", desc: "Standard GST invoice" },
  { value: "DELIVERY_CHALLAN", label: "Delivery challan", desc: "Goods in transit" },
  { value: "CREDIT_NOTE", label: "Credit note", desc: "Returns / adjustments" },
  { value: "DEBIT_NOTE", label: "Debit note", desc: "Additional charges" },
];
const EMPTY_SHIP = { name: "", address: "", city: "", state: "", pincode: "", gstin: "" };
const EMPTY_GOODS = {
  documentType: "TAX_INVOICE", referenceNo: "", referenceDate: "", buyerOrderNo: "", buyerOrderDate: "",
  dispatchDocNo: "", dispatchedThrough: "", destination: "", vehicleNo: "", transportMode: "",
  eWayBillNo: "", eWayBillDate: "",
};

const getTaxType = (userG, clientG) => {
  if (!userG || !clientG) return "";
  return userG.substring(0, 2) === clientG.substring(0, 2) ? "CGST_SGST" : "IGST";
};

// Exact port of v1 calculateTotals()
function calculateTotals(items, taxType, gstType, discountPercent) {
  let subtotal = 0, totalCgst = 0, totalSgst = 0, totalIgst = 0;
  items.forEach((it) => {
    const itemTotal = Number(it.price || 0) * Number(it.qty || 0);
    const gstMult = 1 + it.gstRate / 100;
    subtotal += gstType === "Inclusive" ? itemTotal / gstMult : itemTotal;
  });
  const discountAmount = +((subtotal * Math.min(Math.max(Number(discountPercent || 0), 0), 100)) / 100).toFixed(2);
  const taxableAmount = +(subtotal - discountAmount).toFixed(2);
  items.forEach((it) => {
    const itemTotal = Number(it.price || 0) * Number(it.qty || 0);
    const gstMult = 1 + it.gstRate / 100;
    const itemSub = gstType === "Inclusive" ? itemTotal / gstMult : itemTotal;
    const itemTaxable = subtotal > 0 ? (itemSub / subtotal) * taxableAmount : 0;
    if (taxType === "CGST_SGST") {
      totalCgst += (itemTaxable * (it.gstRate / 2)) / 100;
      totalSgst += (itemTaxable * (it.gstRate / 2)) / 100;
    } else {
      totalIgst += (itemTaxable * it.gstRate) / 100;
    }
  });
  const totalGst = +(totalCgst + totalSgst + totalIgst).toFixed(2);
  const grandTotal = +(taxableAmount + totalGst).toFixed(2);
  return {
    subtotal: +subtotal.toFixed(2), discountAmount, taxableAmount,
    totalCgst: +totalCgst.toFixed(2), totalSgst: +totalSgst.toFixed(2),
    totalIgst: +totalIgst.toFixed(2), totalGst, grandTotal,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
export default function V2CreateInvoice({ showAlert }) {
  usePageTitle("New Invoice");
  const navigate = useNavigate();
  const { user } = useContext(userContext);
  const userGSTIN = user?.companyGSTIN;
  const isNarrow = useMedia("(max-width: 1100px)");
  const { clients, loading: clientsLoading } = useClients();
  const { products, loading: productsLoading, reload: reloadProducts } = useProducts();

  const [businessType, setBusinessType] = useState("service");
  const [salespeople, setSalespeople] = useState([]);
  const [clientId, setClientId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [prefix, setPrefix] = useState(() => { try { return localStorage.getItem(PREFIX_KEY) || ""; } catch { return ""; } });
  const [numBusy, setNumBusy] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [salesperson, setSalesperson] = useState("");
  const [gstType, setGstType] = useState("Exclusive");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [items, setItems] = useState(() => [newLine()]);
  const [goods, setGoods] = useState(EMPTY_GOODS);
  const [shipTo, setShipTo] = useState(EMPTY_SHIP);
  const [shipOpen, setShipOpen] = useState(false);
  const [tried, setTried] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverErr, setServerErr] = useState("");
  const [created, setCreated] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/company`, { headers: auth() }).then((r) => r.json())
      .then((d) => setBusinessType(d.businessType || "service")).catch(() => {});
    fetch(`${API_BASE}/api/salesperson/active`, { headers: auth() }).then((r) => r.json())
      .then((d) => setSalespeople(Array.isArray(d) ? d : [])).catch(() => setSalespeople([]));
  }, []);

  const isGoods = businessType === "goods";
  const client = clients.find((c) => c._id === clientId) || null;
  const taxType = getTaxType(userGSTIN, client?.clientGSTIN);

  const generateNumber = async (pfx = prefix) => {
    setNumBusy(true); setServerErr("");
    try {
      const p = (pfx || "INV").toUpperCase();
      const r = await fetch(`${API_BASE}/api/invoices/getNextInvoiceNumber?prefix=${encodeURIComponent(p)}`, { headers: auth() });
      const d = await r.json();
      if (r.ok && d.invoiceNumber) {
        setInvoiceNumber(d.invoiceNumber);
        try { localStorage.setItem(PREFIX_KEY, p); } catch { /* ignore */ }
      } else setServerErr(d.error || "Couldn't get the next invoice number.");
    } catch { setServerErr("Couldn't reach the server for an invoice number."); }
    finally { setNumBusy(false); }
  };
  // Remembered prefix → number is ready on arrival
  useEffect(() => { if (prefix) generateNumber(prefix); }, []); // eslint-disable-line

  const onClient = (id) => {
    setClientId(id);
    const c = clients.find((x) => x._id === id);
    if (c && !shipTo.name) setShipTo({ name: c.clientName, address: c.clientAddress || "", city: "", state: c.clientState || "", pincode: "", gstin: c.clientGSTIN || "" });
  };

  // Only real lines count (an untouched empty row is ignored)
  const lines = items.filter((it) => it.productId || (it.name && it.name.trim()));
  const totals = useMemo(() => calculateTotals(lines.map((l) => ({ ...l, gstRate: Number(l.gstRate) })), taxType, gstType, discountPercent),
    [lines, taxType, gstType, discountPercent]); // eslint-disable-line
  const stockIssues = stockProblems(lines, products);
  const lineErrors = {};
  lines.forEach((it) => {
    if (!(Number(it.qty) > 0)) lineErrors[it.key] = "Quantity must be at least 1";
    else if (it.price === "" || isNaN(parseFloat(it.price)) || parseFloat(it.price) < 0) lineErrors[it.key] = "Enter a valid rate";
  });

  const checks = [
    { ok: !!clientId, label: "Client chosen" },
    { ok: !!invoiceNumber.trim(), label: "Invoice number" },
    { ok: lines.length > 0 && !Object.keys(lineErrors).length, label: lines.length ? `${lines.length} item${lines.length === 1 ? "" : "s"} ready` : "At least one item" },
    { ok: stockIssues.length === 0, label: stockIssues.length ? "Stock short" : "Stock available" },
  ];
  const ready = checks.every((c) => c.ok);

  const submit = async (e) => {
    e?.preventDefault();
    setTried(true); setServerErr("");
    if (!ready) return;
    const payload = {
      clientId, invoiceDate, invoiceNumber,
      salesperson: salesperson || undefined,
      gstType, taxType,
      discountPercent: Number(discountPercent) || 0,
      items: lines.map((it) => ({
        productId: it.productId || null, name: it.name, hsnCode: it.hsnCode,
        price: Number(it.price), qty: Number(it.qty), gstRate: Number(it.gstRate), unit: it.unit,
        batchNo: it.batchNo || "", expiryDate: it.expiryDate || null,
      })),
      ...(isGoods && {
        invoiceType: "goods", documentType: goods.documentType,
        referenceNo: goods.referenceNo, referenceDate: goods.referenceDate || null,
        buyerOrderNo: goods.buyerOrderNo, buyerOrderDate: goods.buyerOrderDate || null,
        dispatchDocNo: goods.dispatchDocNo, dispatchedThrough: goods.dispatchedThrough,
        destination: goods.destination, vehicleNo: goods.vehicleNo, transportMode: goods.transportMode,
        eWayBillNo: goods.eWayBillNo, eWayBillDate: goods.eWayBillDate || null, shipTo,
      }),
    };
    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/api/invoices/addinvoice`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() }, body: JSON.stringify(payload),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setServerErr(d.error || (d.errors ? d.errors.map((x) => x.msg).join(", ") : "Couldn't create the invoice.")); return; }
      showAlert?.("Invoice created", "success");
      setCreated({ number: invoiceNumber, client: client?.clientName, total: totals.grandTotal, email: client?.clientEmail });
      reloadProducts();
    } catch { setServerErr("Network error — the invoice was not created."); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setCreated(null); setClientId(""); setInvoiceNumber(""); setSalesperson(""); setGstType("Exclusive");
    setDiscountPercent(0); setItems([newLine()]); setGoods(EMPTY_GOODS); setShipTo(EMPTY_SHIP); setTried(false);
    setInvoiceDate(today()); if (prefix) generateNumber(prefix);
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (created) {
    return (
      <div className="v2p v2ci-done">
        <div className="v2ci-done-card">
          <span className="v2ci-done-check"><Glyph name="check" size={34} stroke={2.6} /></span>
          <h1>Invoice {created.number} created</h1>
          <p>{created.client} · <strong>{inr(created.total, { decimals: 2 })}</strong></p>
          {created.email && <p className="v2ci-done-note">A copy is being emailed to {created.email}.</p>}
          <div className="v2ci-done-actions">
            <button type="button" className="v2-btn" onClick={() => navigate("/dashboard/viewinvoices")}><Glyph name="invoice" size={16} /> View invoices</button>
            <button type="button" className="v2-btn v2-btn--primary" onClick={reset}><Glyph name="plus" size={16} stroke={2.2} /> Create another</button>
          </div>
        </div>
      </div>
    );
  }

  const clientItems = clients.map((c) => ({
    id: c._id, label: c.clientName, sub: [c.clientGSTIN, c.clientAddress].filter(Boolean).join(" · "),
    keywords: `${c.clientEmail || ""} ${c.clientPhone || ""}`,
  }));
  const spItems = [{ id: "", label: "Direct sale", sub: "No salesperson" }, ...salespeople.map((s) => ({ id: s._id, label: s.name, sub: s.employeeId }))];
  const g = (k) => (e) => setGoods((p) => ({ ...p, [k]: e.target.value }));
  const sh = (k) => (e) => setShipTo((p) => ({ ...p, [k]: e.target.value }));

  const summary = (
    <aside className="v2-card v2ci-summary" aria-label="Invoice summary">
      <div className="v2ci-sum-head">
        <span className="v2-label">{isGoods ? DOC_TYPES.find((d) => d.value === goods.documentType)?.label : "Tax invoice"}</span>
        <strong className="v2ci-sum-no">{invoiceNumber || "No number yet"}</strong>
        <span className="v2-row-sub">{client ? client.clientName : "No client chosen"}</span>
      </div>
      <div className="v2ci-sum-total">
        <span className="v2-label">Total</span>
        <div className="v2ci-sum-amt"><Money value={totals.grandTotal} /></div>
      </div>
      <div className="v2-inset v2ci-sum-rows">
        <div className="v2-inset-row v2-inset-row--static"><span>Subtotal</span><b>{inr(totals.subtotal, { decimals: 2 })}</b></div>
        {totals.discountAmount > 0 && <div className="v2-inset-row v2-inset-row--static"><span>Discount ({discountPercent}%)</span><b>−{inr(totals.discountAmount, { decimals: 2 })}</b></div>}
        {totals.discountAmount > 0 && <div className="v2-inset-row v2-inset-row--static"><span>Taxable</span><b>{inr(totals.taxableAmount, { decimals: 2 })}</b></div>}
        {taxType === "CGST_SGST" ? (
          <>
            <div className="v2-inset-row v2-inset-row--static"><span>CGST</span><b>{inr(totals.totalCgst, { decimals: 2 })}</b></div>
            <div className="v2-inset-row v2-inset-row--static"><span>SGST</span><b>{inr(totals.totalSgst, { decimals: 2 })}</b></div>
          </>
        ) : (
          <div className="v2-inset-row v2-inset-row--static"><span>IGST{!taxType ? " (until a client is chosen)" : ""}</span><b>{inr(totals.totalIgst, { decimals: 2 })}</b></div>
        )}
      </div>
      <ul className="v2ci-checks">
        {checks.map((c) => (
          <li key={c.label} className={c.ok ? "is-ok" : tried ? "is-bad" : ""}>
            <span className="v2ci-check-dot">{c.ok ? <Glyph name="check" size={11} stroke={3} /> : null}</span>{c.label}
          </li>
        ))}
      </ul>
      {stockIssues.length > 0 && <div className="v2-banner v2-banner--warn">{stockIssues.join(" · ")}</div>}
      {serverErr && <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={16} /> {serverErr}</div>}
      <button type="submit" form="v2-ci-form" className="v2-btn v2-btn--primary v2ci-submit" disabled={submitting}>
        {submitting ? "Creating…" : <>Create invoice · {inr(totals.grandTotal)}</>}
      </button>
      {client?.clientEmail && <p className="v2ci-email-note"><Glyph name="bell" size={13} /> Emailed to {client.clientEmail} on creation</p>}
    </aside>
  );

  return (
    <div className="v2p v2ci">
      <PageHead title="New invoice" sub={isGoods ? "Goods / trading invoice" : "Service invoice"} eyebrow="Receivables" />

      <form id="v2-ci-form" className="v2ci-grid" onSubmit={submit} noValidate>
        <div className="v2ci-main">
          {/* ── Bill to ──────────────────────────────────────────────────── */}
          <section className="v2-card v2ci-sec">
            <div className="v2ci-sec-head"><Duo name="clients" size={26} /><h2>Bill to</h2></div>
            <Combo items={clientItems} value={clientId} onChange={onClient} ariaLabel="Client"
              placeholder={clientsLoading ? "Loading clients…" : "Choose a client"} searchPlaceholder="Search name, GSTIN, email"
              invalid={tried && !clientId}
              footer={<button type="button" className="v2k-combo-footbtn" onClick={() => navigate("/dashboard/manageclients")}><Glyph name="plus" size={14} stroke={2.2} /> New client</button>} />
            {client && (
              <div className="v2ci-client">
                <div>
                  <strong>{client.clientName}</strong>
                  <span>{client.clientAddress}</span>
                  {client.clientGSTIN && <span>GSTIN <span className="v2-mono">{client.clientGSTIN}</span></span>}
                </div>
                <span className={`v2ci-tax${taxType ? "" : " is-unknown"}`}>
                  {taxType === "CGST_SGST" ? "Same state · CGST + SGST" : taxType === "IGST" ? "Inter-state · IGST" : "Tax type unknown — add GSTINs"}
                </span>
              </div>
            )}
          </section>

          {/* ── Details ──────────────────────────────────────────────────── */}
          <section className="v2-card v2ci-sec">
            <div className="v2ci-sec-head"><Duo name="invoice" size={26} /><h2>Details</h2></div>
            <div className="v2ci-fields">
              <Field label="Invoice number" error={tried && !invoiceNumber.trim() ? "Generate or type a number" : ""}>
                <div className="v2ci-num">
                  <input className="v2-input v2ci-prefix" value={prefix} placeholder="INV" maxLength={10}
                    onChange={(e) => setPrefix(e.target.value.toUpperCase())} aria-label="Prefix" />
                  <input className={`v2-input${tried && !invoiceNumber.trim() ? " is-bad" : ""}`} value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="Generate →" aria-label="Invoice number" />
                  <button type="button" className="v2-btn v2-btn--sm" disabled={numBusy} onClick={() => generateNumber()}>
                    {numBusy ? <span className="v2-spin" /> : <Glyph name="refresh" size={15} />} Next
                  </button>
                </div>
              </Field>
              <Field label="Invoice date">
                <input className="v2-input" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
              </Field>
              <Field label="Salesperson" optional>
                <Combo items={spItems} value={salesperson} onChange={setSalesperson} ariaLabel="Salesperson" searchPlaceholder="Search salespeople" />
              </Field>
              <Field label="Prices are" hint={gstType === "Inclusive" ? "GST is extracted from the rates you enter" : "GST is added on top of the rates"}>
                <Segmented ariaLabel="GST mode" value={gstType} onChange={setGstType}
                  options={[{ value: "Exclusive", label: "Excl. GST" }, { value: "Inclusive", label: "Incl. GST" }]} />
              </Field>
            </div>
          </section>

          {/* ── Goods: document type ─────────────────────────────────────── */}
          {isGoods && (
            <section className="v2-card v2ci-sec">
              <div className="v2ci-sec-head"><Duo name="gst" size={26} /><h2>Document type</h2></div>
              <div className="v2ci-doctypes" role="radiogroup" aria-label="Document type">
                {DOC_TYPES.map((d) => (
                  <button key={d.value} type="button" role="radio" aria-checked={goods.documentType === d.value}
                    className={`v2ci-doctype${goods.documentType === d.value ? " is-on" : ""}`}
                    onClick={() => setGoods((p) => ({ ...p, documentType: d.value }))}>
                    <strong>{d.label}</strong><span>{d.desc}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* ── Items ────────────────────────────────────────────────────── */}
          <section className="v2-card v2ci-sec">
            <div className="v2ci-sec-head">
              <Duo name="box" size={26} /><h2>Items</h2>
              <span className="v2-card-caption">{lines.length ? `${lines.length} line${lines.length === 1 ? "" : "s"}` : "Pick from inventory or add a custom line"}</span>
            </div>
            <LineItems items={items} onChange={setItems} products={products} productsLoading={productsLoading}
              stock goods={isGoods} defaultGst={18} errors={tried ? lineErrors : {}} />
            <div className="v2ci-discount">
              <Field label="Discount" hint="Applied before GST, spread across items">
                <div className="v2ci-pct">
                  <input className="v2-input" type="number" min="0" max="100" step="0.01" value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)} aria-label="Discount percent" />
                  <span>%</span>
                </div>
              </Field>
              <div className="v2ci-quickpct">
                {[0, 2, 5, 10].map((p) => (
                  <button key={p} type="button" className={`v2-chip-btn v2i-preset${Number(discountPercent) === p ? " is-on" : ""}`} onClick={() => setDiscountPercent(p)}>{p}%</button>
                ))}
              </div>
            </div>
          </section>

          {/* ── Goods: shipping & dispatch ───────────────────────────────── */}
          {isGoods && (
            <section className={`v2-card v2ci-sec v2ci-collapsible${shipOpen ? " is-open" : ""}`}>
              <button type="button" className="v2ci-sec-head v2ci-sec-toggle" aria-expanded={shipOpen} onClick={() => setShipOpen((o) => !o)}>
                <Duo name="box" size={26} /><h2>Shipping, dispatch & e-way bill</h2>
                <span className="v2-card-caption">{shipTo.name ? `Ship to ${shipTo.name}` : "Optional"}</span>
                <Glyph name="chevronDown" size={18} className="v2i-chev" />
              </button>
              <div className="v2i-collapse"><div>
                <div className="v2ci-subhead">Ship to (consignee)</div>
                <div className="v2ci-fields">
                  <Field label="Name"><input className="v2-input" value={shipTo.name} onChange={sh("name")} /></Field>
                  <Field label="GSTIN" optional><input className="v2-input v2-mono" value={shipTo.gstin} onChange={sh("gstin")} /></Field>
                  <Field label="Address" className="v2ci-wide"><input className="v2-input" value={shipTo.address} onChange={sh("address")} /></Field>
                  <Field label="City"><input className="v2-input" value={shipTo.city} onChange={sh("city")} /></Field>
                  <Field label="State"><input className="v2-input" value={shipTo.state} onChange={sh("state")} /></Field>
                  <Field label="Pincode"><input className="v2-input" inputMode="numeric" value={shipTo.pincode} onChange={sh("pincode")} /></Field>
                </div>
                <div className="v2ci-subhead">References</div>
                <div className="v2ci-fields">
                  <Field label="Reference no." optional><input className="v2-input" value={goods.referenceNo} onChange={g("referenceNo")} /></Field>
                  <Field label="Reference date" optional><input className="v2-input" type="date" value={goods.referenceDate} onChange={g("referenceDate")} /></Field>
                  <Field label="Buyer's order no." optional><input className="v2-input" value={goods.buyerOrderNo} onChange={g("buyerOrderNo")} /></Field>
                  <Field label="Buyer's order date" optional><input className="v2-input" type="date" value={goods.buyerOrderDate} onChange={g("buyerOrderDate")} /></Field>
                  <Field label="Dispatch doc no." optional><input className="v2-input" value={goods.dispatchDocNo} onChange={g("dispatchDocNo")} /></Field>
                  <Field label="Dispatched through" optional><input className="v2-input" value={goods.dispatchedThrough} onChange={g("dispatchedThrough")} /></Field>
                </div>
                <div className="v2ci-subhead">Transport</div>
                <div className="v2ci-fields">
                  <Field label="Destination" optional><input className="v2-input" value={goods.destination} onChange={g("destination")} /></Field>
                  <Field label="Mode" optional>
                    <Segmented size="sm" ariaLabel="Transport mode" value={goods.transportMode}
                      onChange={(v) => setGoods((p) => ({ ...p, transportMode: v }))}
                      options={[{ value: "", label: "—" }, { value: "1", label: "Road" }, { value: "2", label: "Rail" }, { value: "3", label: "Air" }, { value: "4", label: "Ship" }]} />
                  </Field>
                  <Field label="Vehicle no." optional><input className="v2-input v2-mono" value={goods.vehicleNo} onChange={g("vehicleNo")} /></Field>
                  <Field label="E-way bill no." optional><input className="v2-input v2-mono" value={goods.eWayBillNo} onChange={g("eWayBillNo")} /></Field>
                  <Field label="E-way bill date" optional><input className="v2-input" type="date" value={goods.eWayBillDate} onChange={g("eWayBillDate")} /></Field>
                </div>
              </div></div>
            </section>
          )}

          {isNarrow && summary}
        </div>
        {!isNarrow && <div className="v2ci-side">{summary}</div>}
      </form>

      {/* Phone/tablet: total + create always reachable */}
      {isNarrow && (
        <div className="v2ci-dock">
          <div><span className="v2-label">Total</span><strong>{inr(totals.grandTotal, { decimals: 2 })}</strong></div>
          <button type="submit" form="v2-ci-form" className="v2-btn v2-btn--primary" disabled={submitting}>{submitting ? "Creating…" : "Create"}</button>
        </div>
      )}
    </div>
  );
}
